import React from "react";
import { motion } from "framer-motion";
import { GlobeHemisphereWest } from "@phosphor-icons/react";
import { WorldMap } from "@/components/WorldMap";

const FLAG = {
  "United States": "🇺🇸",
  "United Kingdom": "🇬🇧",
  "United Arab Emirates": "🇦🇪",
  "Malaysia": "🇲🇾",
  "Indonesia": "🇮🇩",
  "India": "🇮🇳",
  "Singapore": "🇸🇬",
  "Australia": "🇦🇺",
  "Canada": "🇨🇦",
  "Germany": "🇩🇪",
  "France": "🇫🇷",
  "Spain": "🇪🇸",
  "Italy": "🇮🇹",
  "Brazil": "🇧🇷",
  "Mexico": "🇲🇽",
  "China": "🇨🇳",
  "Japan": "🇯🇵",
  "Saudi Arabia": "🇸🇦",
  "South Africa": "🇿🇦",
  "Netherlands": "🇳🇱",
  "Belgium": "🇧🇪",
  "Hong Kong": "🇭🇰",
  "Hong Kong SAR": "🇭🇰",
  "South Korea": "🇰🇷",
  "Philippines": "🇵🇭",
};

const flagFor = (c) => {
  if (FLAG[c]) return FLAG[c];
  const k = Object.keys(FLAG).find((f) => c && c.includes(f));
  return k ? FLAG[k] : "🌐";
};

export const CountryExplorer = ({ countryCounts, total, active, onSelect }) => {
  if (!countryCounts || countryCounts.length === 0) return null;

  return (
    <section className="mx-auto max-w-7xl px-5 pt-12 sm:px-8" data-testid="country-explorer">
      <div className="mb-4 flex items-center gap-2">
        <GlobeHemisphereWest size={18} weight="duotone" className="text-blue-400" />
        <h2 className="font-display text-lg font-bold tracking-tight text-white">Browse by Country</h2>
        <span className="text-xs uppercase tracking-[0.16em] text-slate-500">{countryCounts.length} markets worldwide</span>
      </div>

      <div className="mb-5">
        <WorldMap countryCounts={countryCounts} active={active} onSelect={onSelect} />
      </div>

      <div className="flex flex-wrap gap-2.5" data-testid="country-chips">
        <Chip
          testid="country-chip-all"
          label="All Countries"
          flag="🌍"
          count={total}
          active={active === "all"}
          onClick={() => onSelect("all")}
        />
        {countryCounts.map((c, i) => (
          <Chip
            key={c.country}
            testid={`country-chip-${c.country.replace(/\s+/g, "-").toLowerCase()}`}
            label={c.country}
            flag={flagFor(c.country)}
            count={c.count}
            active={active === c.country}
            onClick={() => onSelect(c.country)}
            index={i}
          />
        ))}
      </div>
    </section>
  );
};

const Chip = ({ testid, label, flag, count, active, onClick, index = 0 }) => (
  <motion.button
    data-testid={testid}
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay: Math.min(index * 0.03, 0.3) }}
    onClick={onClick}
    className={`group inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 ${
      active
        ? "border-blue-500 bg-blue-500/15 text-white"
        : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/25 hover:text-white"
    }`}
  >
    <span className="text-base leading-none">{flag}</span>
    {label}
    <span
      className={`ml-0.5 rounded-full px-2 py-0.5 text-[11px] font-bold ${
        active ? "bg-blue-500 text-white" : "bg-white/10 text-slate-400 group-hover:text-white"
      }`}
    >
      {count}
    </span>
  </motion.button>
);
